import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Form from 'components/elements/Form/form';
import TrainingService, { TrainerProps } from 'services/hooks/trainer-service';
import Input from 'components/elements/Form/Input/input';
import LoginIcon from 'assets/Icons/loginIcons/loginIcon';
import SelectEntity from '../Common/ListsEntity/SelectEntity';
import OccupantService from 'services/hooks/occupant-service';
import SpaceService from 'services/hooks/space-service';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import useSaveTrainer from './crud/useSave';
import { formatRFC3339 } from 'date-fns';
import Logout from 'components/structure/Logout/Logout';
import MessageIcon from '@mui/icons-material/Message';
import Modal from 'components/elements/Modal';
import MessageOccupant from 'components/structure/Notification/MessageOccupant';
import { colours } from 'utils/constants';
import { Grid, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';

export default function TrainerForm() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [startTime, setStartTime] = useState<Date | null>();
  const [endTime, setEndTime] = useState<Date | null>();
  const [openMessageModal, setopenMessageModal] = useState(false);

  /* The `useForm` hook from the `react-hook-form` library is being used to initialize a form instance. 
  This form instance provides methods and state for managing form inputs, validation, and submission. 
  The `methods` object returned by `useForm` includes properties like `control`, `handleSubmit`, `formState`, and more. 
  These methods can be used to control and interact with the form in the component. */
  const methods = useForm();

  /* The `saveTrainerMutation` variable is created using the `useSaveTrainer` hook. This hook is used to
  handle the create mutation of a Trainer. It takes in several parameters: */
  const saveTrainerMutation = useSaveTrainer({
    createQuery: TrainingService.createTrainer,
    queryName: 'trainer',
    onSuccess: () => {
      methods.reset({
        mobileUserUuid: null,
        spaceUuid: null,
        tag: null
      });
      setStartTime(null);
      setEndTime(null);
    }
  });

  /* The `onSubmit` function takes a `TrainerProps` object as an argument, extracts specific properties from it, and passes them to the 
  `saveTrainerMutation.mutate` function. */
  const onSubmit = async (e: TrainerProps) => {
    saveTrainerMutation.mutate({
      mobileUserUuid: e.mobileUserUuid,
      spaceUuid: e.spaceUuid,
      tag: e.tag,
      start: startTime && formatRFC3339(new Date(startTime)),
      end: endTime && formatRFC3339(new Date(endTime))
    });
  };

  return (
    <>
      <div className="flex my-10 justify-end">
        <div className="flex items-center space-x-2 md:space-x-4 ml-2 md:ml-4">
          <Grid item>
            {isSmallScreen ? (
              <Tooltip title="Message Occupant">
                <IconButton
                  onClick={() => setopenMessageModal(true)}
                  size="large"
                  sx={{
                    backgroundColor: '#FFFFFF',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                  }}
                >
                  <MessageIcon
                    sx={{
                      color: colours.seafoam
                    }}
                  />
                </IconButton>
              </Tooltip>
            ) : (
              <SecondaryButton
                buttonLabel="Message Occupant"
                iconLeft={<MessageIcon sx={{ color: colours.seafoam }} />}
                onClick={() => setopenMessageModal(true)}
                variant="default"
                size="px-4 w-auto !bg-primary2"
              />
            )}
          </Grid>
          <Grid item>
            <Logout />
          </Grid>
          {/* <Logout /> */}
        </div>
      </div>
      <Modal
        isOpen={openMessageModal}
        onRequestClose={() => setopenMessageModal(false)}
        title="Send Message to Occupants"
      >
        <MessageOccupant setShowModal={setopenMessageModal} />
      </Modal>
      <h1>Trainer</h1>

      <div className="flex">
        <Form
          buttonLabel="Submit"
          icon={<LoginIcon />}
          methods={methods}
          onSubmit={onSubmit}
          styles="text-left bg-neutral1 p-0 sm:p-20"
        >
          <SelectEntity
            Controller={Controller}
            control={methods.control}
            label="Name"
            placeholder="Select Name"
            queryname="occupants"
            name="mobileUserUuid"
            errors={methods.formState.errors}
            query={OccupantService.findAllOccupants}
            expectedLabel="FullName"
          />
          <br />
          <SelectEntity
            Controller={Controller}
            control={methods.control}
            label="Space"
            placeholder="Select Space"
            queryname="space"
            name="spaceUuid"
            errors={methods.formState.errors}
            query={SpaceService.findAllSpaces}
            expectedLabel="name"
          />
          <br />
          <Input
            label="Tag"
            name="tag"
            type="text"
            // data-testid="example"
            placeholder="Bline"
            autoFocus
          />
          <br />
          <SecondaryButton
            buttonLabel="Start Time"
            disabled={!!startTime}
            iconRight={startTime ? `: ${startTime.toLocaleString()}` : ''}
            variant="default"
            size="px-4 w-auto"
            onClick={() => {
              setStartTime(new Date());
            }}
          />
          <br />
          <SecondaryButton
            buttonLabel="End Time"
            disabled={!startTime || (!!startTime && !!endTime)}
            iconRight={endTime ? `: ${endTime.toLocaleString()} ` : ''}
            variant="default"
            size="px-4 w-auto"
            onClick={() => {
              setEndTime(new Date());
            }}
          />
        </Form>
      </div>
    </>
  );
}
